import React from 'react';
import './WhoWeArePage.css';
import greenBullet from '../assets/images/green-bullet.svg';

const WhoWeArePage = () => {
  // Guiding Principles section data
  const guidingPrinciples = [
    {
      title: 'Character counts',
      description: 'We start with honesty integrity and candor. We expect these qualities from our investors our management teams and our employees.'
    },
    {
      title: 'Diversity and inclusion breed strength',
      description: 'We seek out new and different viewpoints to strengthen our firm and foster robust teamwork and discussion that builds better companies and ultimately better outcomes.'
    },
    {
      title: 'Strive for excellence',
      description: 'We seek out the best people for our team and our portfolio. We set high standards and work hard with humility and integrity in an effort to generate exceptional results.'
    },
    {
      title: 'Stronger partnerships make better companies',
      description: 'We believe BayPine represents a new kind of private equity. Our joy comes from building deeper relationships and being hands-on with our portfolio companies.'
    },
    {
      title: 'Judgment matters',
      description: 'There is no substitute for careful diligence hard-won experience and well-reasoned investment judgment.'
    },
    {
      title: 'It\'s a privilege to serve our limited partners',
      description: 'Our investors make it possible for us to future-proof strong companies to face the challenges of tomorrow.'
    }
  ];

  return (
    <div className="who-we-are-page">
      {/* Hero Section */}
      <div
        className="who-we-are-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(13, 21, 38, 0.4), rgba(13, 21, 38, 0.4)), url(https://d362w0t6uix91v.cloudfront.net/Assets/Hero-Images/Who-we-are.jpg)`
        }}
      >
        {/* <div className="hero-content"> */}
          <div className="page-headline">
            <p>Our passion is partnership</p>
          </div>
          <div className="page-intro">
            <p>BayPine is dedicated to hands-on investing.</p>
          </div>
        {/* </div> */}
      </div>

      {/* Guiding Principles Section */}
      <section className="guiding-principles-section">
          <div className="guiding-principles-header">
            <h2 className="section-label">
              <img className="bullet-image" src={greenBullet} alt="" />
              GUIDING PRINCIPLES
            </h2>
            <p className="section-description">
              We are deeply committed to our communities and strive to be a force for good in the world.
            </p>
          </div>

          <div className="guiding-principles-content">
            <div className="flourish">
              <div className="line"></div>
              <div className="ellipse"></div>
            </div>

            <div className="principles-grid">
              {guidingPrinciples.map((principle, index) => (
                <div key={index} className="principle-item">
                  <h4 className="principle-title">{principle.title}</h4>
                  <p className="principle-description">{principle.description}</p>
                </div>
              ))}
            </div>
          </div>
      </section>

      {/* Stakeholders Over Shareholders Section */}
      <section className="stakeholders-section" style={{
        backgroundImage: "url('https://d362w0t6uix91v.cloudfront.net/Assets/Trees.jpg')",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover"
      }}>
          <div className="stakeholders-content">
            <p className="stakeholders-text">
              Values-driven, patient, and responsible investing benefits all stakeholders: investors, companies, and communities.
            </p>
            <a className="stakeholders-link" href="/approach">
              LEARN ABOUT OUR APPROACH
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.7387 12.6259L13.4506 17.0037C13.2898 17.1679 13.0754 17.25 12.861 17.25C12.6198 17.25 12.4054 17.1679 12.2446 17.0037C11.8961 16.6754 11.8961 16.1008 12.2446 15.7725L15.0586 12.8721H6.85762C6.37521 12.8721 6 12.4891 6 11.9966C6 11.5314 6.37521 11.121 6.85762 11.121H15.0586L12.2446 8.24805C11.8961 7.91971 11.8961 7.34511 12.2446 7.01678C12.5662 6.66107 13.129 6.66107 13.4506 7.01678L17.7387 11.3946C18.0871 11.723 18.0871 12.2976 17.7387 12.6259Z" fill="currentColor" />
              </svg>
            </a>
          </div>
      </section>

      {/* Our Commitment Section */}
      <section className="commitment-section">
          <div className="commitment-header">
            <h2 className="section-label">
              <img className="bullet-image" src={greenBullet} alt="" />
              OUR COMMITMENT
            </h2>
          </div>

          <div className="commitment-content">
            {/* ESG Commitment */}
            <div className="commitment-item">
              <div className="commitment-text">
                <h3 className="commitment-title">ESG</h3>
                <p className="commitment-description">
                  We integrate environmental, social, and governance (ESG) considerations into investment and operating decisions to mitigate risk, unlock opportunity, and create value. Our ESG Program is designed to uphold standards of responsible investment and business ethics.
                </p>

                <div className="initiative">
                  <div className="initiative-logo">
                    <img src="https://d362w0t6uix91v.cloudfront.net/Assets/Logos/IC-Logo.svg" alt="Initiative Climat International" />
                  </div>
                  <p className="initiative-description">
                    In 2022, we became a signatory to the Initiative Climat International (iCI), an initiative supported by the Principles for Responsible Investment, which seeks to better understand and manage the risks associated with climate change.
                  </p>
                </div>
              </div>

              <div className="commitment-accolade">
                <img src="https://d362w0t6uix91v.cloudfront.net/Assets/Logos/PE-Innovators-in-ESG-Awards.jpeg" alt="ESG Award" className="accolade-image" />
                <p className="accolade-description">2022 Mergers & Acquisitions Private Equity Innovator in ESG*</p>
              </div>
            </div>

            {/* DEI Commitment */}
            <div className="commitment-item">
              <div className="commitment-text">
                <h3 className="commitment-title">Diversity, Equity & Inclusion</h3>
                <p className="commitment-description">
                  We believe that a diverse team and an inclusive culture fosters innovative perspectives and are foundational to the success of our firm and our portfolio companies.
                </p>

                <div className="initiative">
                  <div className="initiative-logo">
                    <img src="https://d362w0t6uix91v.cloudfront.net/Assets/Logos/ILPA.svg" alt="ILPA" />
                    <img src="https://d362w0t6uix91v.cloudfront.net/Assets/Logos/ILPA-DIA-External-CMYK_Dark.png" alt="ILPA Diversity in Action" />
                  </div>
                  <p className="initiative-description">
                    In 2021, we became a signatory to the Institutional Limited Partners Association's ("ILPA") Diversity in Action initiative, which brings together limited partners and general partners who share a commitment to advancing diversity, equity, and inclusion in the private equity industry. The goal of the initiative is to motivate market participants to engage in the journey towards becoming more diverse and inclusive and to build momentum around the adoption of specific actions that advance DEI over time.
                  </p>
                </div>

                <div className="initiative">
                  <div className="initiative-logo">
                    <img src="https://d362w0t6uix91v.cloudfront.net/Assets/Logos/GEC_2023badge_round-Copy.png" alt="Gender Equity Consortium" />
                  </div>
                  <p className="initiative-description">
                    In 2023, BayPine became a founding member of the Gender Equity Consortium, a program created in partnership with Dartmouth's Tuck School of Business designed to empower the next generation of female private market leaders and enhance gender equity in the industry. As part of our commitment, BayPine will identify one policy to implement within our firm, track success, and report back to the consortium.
                  </p>
                </div>
              </div>

              <div className="commitment-accolade">
                <img src="https://d362w0t6uix91v.cloudfront.net/Assets/Logos/dei.png" alt="DEI Award" className="accolade-image" />
                <p className="accolade-description">2022 Mergers & Acquisitions Leader in Diversity, Equity & Inclusion*</p>
              </div>
            </div>

            <p className="commitment-disclaimer">
              * All awards and recognitions presented herein are presented by third parties that are not affiliated with BayPine and were issued for 2022. For each recognition, BayPine is assessed based on written responses to questions on each respective application submitted by BayPine. BayPine does not know the exact number of managers surveyed for a given award or recognition, and therefore does not know the percentage of investors that received any one award or recognition. These recognitions are not indicative of BayPine’s future performance and do not reflect the experience of BayPine or its investment professionals. BayPine did not provide compensation to the third-party ranking organizations to participate in the ranking.
            </p>
          </div>
      </section>

      {/* Video Section */}
      <section className="video-section">
          <div className="video-header">
            <h2 className="section-label">
              <img className="bullet-image" src={greenBullet} alt="" />
              WHO WE ARE
            </h2>
          </div>

          <div className="video-container">
            <iframe
              src="https://player.vimeo.com/video/771299895"
              title="BayPine: Who We Are"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
          <div className="contact-grid">
            <div className="contact-item talk-with-us">
              <h2 className="section-label">
                <img className="bullet-image" src={greenBullet} alt="" />
                TALK WITH US
              </h2>
              <p className="contact-description">
                We're always eager to make new connections and provide more information.
              </p>
              <a href="/contact" className="contact-link">
                GET IN TOUCH
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.7387 12.6259L13.4506 17.0037C13.2898 17.1679 13.0754 17.25 12.861 17.25C12.6198 17.25 12.4054 17.1679 12.2446 17.0037C11.8961 16.6754 11.8961 16.1008 12.2446 15.7725L15.0586 12.8721H6.85762C6.37521 12.8721 6 12.4891 6 11.9966C6 11.5314 6.37521 11.121 6.85762 11.121H15.0586L12.2446 8.24805C11.8961 7.91971 11.8961 7.34511 12.2446 7.01678C12.5662 6.66107 13.129 6.66107 13.4506 7.01678L17.7387 11.3946C18.0871 11.723 18.0871 12.2976 17.7387 12.6259Z" fill="currentColor" />
                </svg>
              </a>
            </div>

            <div className="contact-item join-us">
              <h2 className="section-label">
                <img className="bullet-image" src={greenBullet} alt="" />
                JOIN US
              </h2>
              <p className="contact-description">
                Join our dynamic and ambitious team.
              </p>
              <a href="mailto:Careers@BayPine.com" className="contact-link">
                INQUIRE ABOUT AVAILABLE OPPORTUNITIES
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.7387 12.6259L13.4506 17.0037C13.2898 17.1679 13.0754 17.25 12.861 17.25C12.6198 17.25 12.4054 17.1679 12.2446 17.0037C11.8961 16.6754 11.8961 16.1008 12.2446 15.7725L15.0586 12.8721H6.85762C6.37521 12.8721 6 12.4891 6 11.9966C6 11.5314 6.37521 11.121 6.85762 11.121H15.0586L12.2446 8.24805C11.8961 7.91971 11.8961 7.34511 12.2446 7.01678C12.5662 6.66107 13.129 6.66107 13.4506 7.01678L17.7387 11.3946C18.0871 11.723 18.0871 12.2976 17.7387 12.6259Z" fill="currentColor" />
                </svg>
              </a>
            </div>
          </div>
      </section>
    </div>
  );
};

export default WhoWeArePage;
